/**
 * The high-lift "Tailor" workbench TUTORIAL — a guided, interactive tour (FXC-9009).
 *
 * Walks a first-time user through FOUR real edits that prove the section is genuinely malleable, not a
 * fixed picture:
 *   1. Replace the base foil (LS(1)-0417 → NACA 4412/0012) — the whole section re-derives.
 *   2. Add a cove (a leading-edge slat carves a new coved slot into the main).
 *   3. Change the aileron (re-deflect it in the rigging table).
 *   4. Swap the aileron for a FRISE aileron (the protruding lower-nose horn).
 * …then points at Solve.
 *
 * This drives driver.js DIRECTLY rather than going through the shared `OnboardingProvider`: each step
 * must gate "Next" on the LIVE foil-design state (digest of the swapped foil, element count, aileron
 * deflection, frise flag), and the shared provider's React-context + module-level challenge registry
 * proved unreliable to consume from this surface under Vite's dev module duplication (the hook bound
 * to a second, empty context instance). A self-contained driver keeps the tour robust and local: it
 * reads a `stateRef` the surface refreshes every render, `setStage('rig')`s into the Design stage
 * before each step, and polls the active step's challenge to enable/disable the Next button.
 */
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export const HIGHLIFT_TOUR_ID = 'highlift-tailor';

/** Live design signals the tour challenges poll. The surface refreshes `live` on every render. */
export interface HighliftTourLive {
  /** Digest of the main element's base coords — changes iff the base foil is swapped. */
  foilDigest: number;
  /** Number of section elements — increases when a cove/slat/flap is added. */
  elementCount: number;
  /** The aileron's (flaperon) deflection in degrees — the "change the aileron" signal. */
  aileronDeflDeg: number;
  /** Whether any aileron element has the Frise horn enabled. */
  friseOn: boolean;
}

/** Tour state: the live signals plus a baseline snapshot captured the moment the tour starts. */
export interface HighliftTourState {
  live: HighliftTourLive;
  baseline: HighliftTourLive | null;
}

export type HighliftTourStateRef = { current: HighliftTourState };

/** A fresh, zeroed live snapshot (used to seed the ref before the first render fills it in). */
export function emptyTourLive(): HighliftTourLive {
  return { foilDigest: 0, elementCount: 0, aileronDeflDeg: 0, friseOn: false };
}

/** A tour step: a driver popover + (optionally) a challenge predicate that gates "Next". */
interface HighliftStep {
  element: string;
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** When present, Next stays disabled until this returns true (read against the live state). */
  challenge?: (s: HighliftTourState) => boolean;
  /** Imperative prep before the step highlights (open folds/menus its target
   *  lives in — the rail's add-element menu is a <details>, review fix). */
  prepare?: () => void;
}

/** Open the rail's "+ add element" <details> so the tour's slat target is
 *  visible + clickable when the add-cove step highlights. */
function openAddElementMenu(): void {
  const menu = document.querySelector('.hlw-addel-menu');
  if (menu instanceof HTMLDetailsElement) menu.open = true;
}

function buildSteps(stateRef: HighliftTourStateRef): HighliftStep[] {
  const base = () => stateRef.current.baseline; // non-null once the tour has started
  return [
    {
      element: '[data-tour="hlw-spine"]',
      title: 'Tailor the section, hands-on',
      description:
        'This workbench is a live multi-element section, not a picture. We’ll prove it with four real edits — swap the foil, add a cove, re-deflect the aileron, then make it a Frise aileron. Each step unlocks “Next” once you’ve actually done it.',
      side: 'bottom',
      align: 'start',
    },
    {
      element: '[data-tour="hlw-replace-foil"]',
      title: '1 · Replace the foil',
      description:
        'Pick a different base airfoil from this dropdown (e.g. NACA 4412). The whole section — main contour, aileron nose, every slot — re-derives off the new foil. That’s the proof the geometry is real.',
      side: 'right',
      challenge: (s) => base() != null && s.live.foilDigest !== s.baseline!.foilDigest,
    },
    {
      element: '[data-tour="hlw-add-cove"]',
      title: '2 · Add a cove',
      description:
        'Open “+ add element” and click “+ slat”. A leading-edge slat carves a new coved slot into the main element — a fresh cove appears in the rigging table with its own gap/overlap readout.',
      side: 'right',
      challenge: (s) => base() != null && s.live.elementCount > s.baseline!.elementCount,
      prepare: openAddElementMenu,
    },
    {
      element: '[data-tour="hlw-rigging"]',
      title: '3 · Change the aileron',
      description:
        'In the rigging table, edit the aileron row’s Deflection. The deployed contour rotates about its hinge live as you type.',
      side: 'left',
      challenge: (s) => base() != null && Math.abs(s.live.aileronDeflDeg - s.baseline!.aileronDeflDeg) > 0.5,
    },
    {
      element: '[data-tour="hlw-frise-toggle"]',
      title: '4 · Make it a Frise aileron',
      description:
        'Flip the aileron variant from Plain to Frise. A protruding lower-nose horn grows ahead of the hinge — on trailing-edge-up deflection it swings below the wing, the classic Frise adverse-yaw balance.',
      side: 'left',
      challenge: (s) => s.live.friseOn,
    },
    {
      element: '[data-tour="hlw-solve"]',
      title: 'Now solve it',
      description:
        'You reshaped the section four ways. Hit Solve to run this configuration (NN warm-started) and watch the polar update. That’s the loop: tailor → solve → trust.',
      side: 'bottom',
    },
  ];
}

let activeDriver: Driver | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPolling(): void {
  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * Start the workbench tutorial. Snapshots the current design as the baseline (so the step challenges
 * measure change from this moment), moves the workbench into the Design stage, and drives driver.js.
 */
export function startHighliftTour(opts: {
  stateRef: HighliftTourStateRef;
  setStage: (stage: 'rig') => void;
  /** Force-expand the model rail (it defaults COLLAPSED since the calm-down —
   *  review fix: tour steps 1-2 target rail content that would be unmounted). */
  expandRail?: () => void;
}): void {
  if (activeDriver) return;
  const { stateRef, setStage, expandRail } = opts;
  stateRef.current.baseline = { ...stateRef.current.live };
  setStage('rig');
  expandRail?.();

  const steps = buildSteps(stateRef);
  const d = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} of {{total}}',
    allowClose: true,
    stagePadding: 8,
    stageRadius: 8,
    popoverClass: 'thread-tour-popover',
    steps: steps.map((s) => ({
      element: s.element,
      popover: { title: s.title, description: s.description, side: s.side, align: s.align },
      onHighlightStarted: () => {
        setStage('rig');
        expandRail?.(); // rail targets must stay mounted on every step
        s.prepare?.();
      },
    })),
    onDestroyed: () => {
      stopPolling();
      activeDriver = null;
    },
  });
  activeDriver = d;

  // Gate "Next" on each step's challenge: poll the live state and enable/disable the button.
  pollTimer = setInterval(() => {
    if (!activeDriver) {
      stopPolling();
      return;
    }
    const i = activeDriver.getActiveIndex() ?? 0;
    const step = steps[i];
    const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLButtonElement | null;
    if (!nextBtn) return;
    const done = step?.challenge ? step.challenge(stateRef.current) : true;
    nextBtn.disabled = !done;
    nextBtn.classList.toggle('driver-popover-btn--disabled', !done);
    nextBtn.style.opacity = done ? '' : '0.45';
    nextBtn.title = done ? '' : 'Do the step above to continue';
  }, 200);

  d.drive(0);
}

/**
 * The "Tutorial" launch chip for the workbench spine. `onStart` runs the self-contained tour (the
 * surface wires it to {@link startHighliftTour} with its live `stateRef` + `setStage`).
 */
export function TutorialButton({ onStart, className }: { onStart: () => void; className?: string }) {
  return (
    <span
      className={className ?? 'hlw-cond'}
      role="button"
      tabIndex={0}
      data-tour="hlw-tutorial-launch"
      aria-label="Start the workbench tutorial"
      title="Guided tutorial: tailor the section in four edits"
      style={{ cursor: 'pointer' }}
      onClick={onStart}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStart();
        }
      }}
    >
      ? Tutorial
    </span>
  );
}
